export type CreditCostSource = 'local-estimate' | 'unknown';

export interface CreditCostEstimate {
  credits?: number;
  source: CreditCostSource;
  detail: string;
}

type McpCost = { cost: number; detailedCost?: number };

const MCP_COSTS: Record<string, McpCost> = {
  get_announcement_categories: { cost: 0 },
  get_announcements: { cost: 1, detailedCost: 2 },
  get_concalls: { cost: 2, detailedCost: 3 },
  get_daily_summary: { cost: 1 },
  get_earnings_filing: { cost: 3 },
  get_events: { cost: 1 },
  get_news: { cost: 1 },
  get_price_and_volume: { cost: 1 },
  get_price_and_volume_since: { cost: 2 },
  get_sectoral_data: { cost: 1 },
  get_sector_wise_companies: { cost: 1 },
  get_52w_data: { cost: 2 },
  get_symbols: { cost: 1 },
  get_top_movers: { cost: 1 },
  get_top_traded: { cost: 1 },
  list_earnings: { cost: 2, detailedCost: 3 },
  resolve_symbols: { cost: 1 },
  search_announcements: { cost: 1 },
  search_concalls: { cost: 2 },
};

const REST_RULES: Record<string, string> = {
  '/v1/news': '1 credit per page when results are returned.',
  '/v1/symbols/metadata': '1 credit per request with Symbols product scope.',
  '/v1/announcements/categories': 'Free.',
  '/v1/announcements': 'detailed=false: 1 credit per returned item; detailed=true: 2 credits per returned item.',
  '/v1/announcements/attachments': '1 credit per ready item returned.',
  '/v1/earnings': 'detailed=false: 1 credit per returned item; detailed=true: 2 credits per returned item.',
  '/v1/earnings/index': 'Lightweight earnings list: 1 credit per returned item.',
  '/v1/earnings/upcoming': 'Free.',
  '/v1/earnings/detail': '3 credits per request.',
  '/v1/earnings/attachments': '1 credit per ready item returned.',
  '/v1/concalls': 'detailed=false: 1 credit per returned item; detailed=true: 2 credits per returned item.',
  '/v1/concalls/index': 'Lightweight concall list: 1 credit per returned item.',
  '/v1/concalls/upcoming': 'detailed=false: free; detailed=true: 5 credits per page with results.',
  '/v1/concalls/detail': '3 credits per request.',
  '/v1/concalls/transcript': '4 credits per ready transcript or recording URL.',
  '/v1/concalls/transcripts': '4 credits per ready transcript or recording URL delivered by POST batch.',
  '/v1/alerts': '5 credits per page when results are returned.',
  '/v1/daily-summary/': '5 credits after a successful generation.',
  '/v1/batch-jobs': 'Job accepted: 50 credits; each successful portfolio summary: 5 credits asynchronously.',
};

export function estimateRestCredits(
  endpoint: string,
  params: Record<string, unknown>,
  body: unknown,
): CreditCostEstimate {
  const count = returnedItemCount(body);
  const ready = readyItemCount(body);
  const detailed = boolParam(params.detailed);

  switch (endpoint) {
    case '/v1/news':
      return known(count > 0 ? 1 : 0, REST_RULES[endpoint]);
    case '/v1/symbols/metadata':
      return known(1, REST_RULES[endpoint]);
    case '/v1/announcements/categories':
    case '/v1/earnings/upcoming':
      return known(0, REST_RULES[endpoint]);
    case '/v1/announcements':
    case '/v1/earnings':
    case '/v1/concalls':
      return known(count * (detailed ? 2 : 1), REST_RULES[endpoint]);
    case '/v1/earnings/index':
    case '/v1/concalls/index':
      return known(count, REST_RULES[endpoint]);
    case '/v1/announcements/attachments':
    case '/v1/earnings/attachments':
      return known(ready, REST_RULES[endpoint]);
    case '/v1/earnings/detail':
    case '/v1/concalls/detail':
      return known(3, REST_RULES[endpoint]);
    case '/v1/concalls/transcript':
      return known(ready > 0 ? 4 * ready : 4, REST_RULES[endpoint]);
    case '/v1/concalls/transcripts':
      return known(4 * ready, REST_RULES[endpoint]);
    case '/v1/concalls/upcoming':
      return known(detailed && count > 0 ? 5 : 0, REST_RULES[endpoint]);
    case '/v1/alerts':
      return known(count > 0 ? 5 : 0, REST_RULES[endpoint]);
    case '/v1/daily-summary/':
      return known(hasGeneratedSummary(body) ? 5 : 0, REST_RULES[endpoint]);
    default:
      return { source: 'unknown', detail: `No credit baseline for ${endpoint}` };
  }
}

export function estimateMcpCredits(name: string, args: Record<string, unknown>): CreditCostEstimate {
  const cost = MCP_COSTS[name];
  if (!cost) {
    return { source: 'unknown', detail: `No MCP credit baseline for ${name}` };
  }

  const detailed = boolParam(args.detailed);
  const credits = detailed && typeof cost.detailedCost === 'number' ? cost.detailedCost : cost.cost;
  return known(credits, `MCP ${name}: ${detailed ? 'detailed ' : ''}${credits} credit${credits === 1 ? '' : 's'}.`);
}

function known(credits: number, detail = 'Drishti pricing baseline.'): CreditCostEstimate {
  return { credits, source: 'local-estimate', detail };
}

function boolParam(value: unknown): boolean {
  return value === true || value === 'true';
}

function returnedItemCount(value: unknown): number {
  const data = unwrapData(value);
  if (Array.isArray(data)) return data.length;
  if (data && typeof data === 'object' && Array.isArray((data as { data?: unknown }).data)) {
    return ((data as { data: unknown[] }).data).length;
  }
  return 0;
}

function readyItemCount(value: unknown): number {
  const data = unwrapData(value);
  const rows = Array.isArray(data)
    ? data
    : data && typeof data === 'object' && Array.isArray((data as { data?: unknown }).data)
      ? (data as { data: unknown[] }).data
      : [];

  return rows.filter((row) => {
    if (!row || typeof row !== 'object') return false;
    const obj = row as Record<string, unknown>;
    return obj.status === 'ready' || typeof obj.url === 'string' || typeof obj.transcript_url === 'string' || typeof obj.audio_url === 'string';
  }).length;
}

function hasGeneratedSummary(value: unknown): boolean {
  const data = unwrapData(value);
  if (!data || typeof data !== 'object') return false;
  const obj = data as Record<string, unknown>;
  return obj.status === 'success' && typeof obj.summary === 'string' && obj.summary.trim().length > 0;
}

function unwrapData(value: unknown): unknown {
  if (value && typeof value === 'object' && 'data' in value) {
    return (value as { data?: unknown }).data;
  }
  return value;
}
